import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { InspectionTemplate } from '../../../../../common/src/domain/inspectionTemplate/inspectionFormTableSchema';
import { useTranslate } from '../../../mixins/translate/translate';
import { fetchGet } from '../../../store/fetch';
import { openApiFetchDelete } from '../../../store/openApiFetch';
import { useOpenApiFetch } from '../../../store/useOpenApiFetch';
import { Roles } from '../../../utils/enums/Roles';
import { IconEditButton } from '../../common/iconButtons/IconEditButton';
import { IconRemoveButton } from '../../common/iconButtons/IconRemoveButton';
import {
    Content,
    EditPanelSection,
    EditPanelSelect,
    PanelBody,
    PanelRouterLink,
    PanelWithoutForm,
    RightTray,
    Table,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    Topbar,
    TopbarCrumbActive,
    TopbarLeft,
    TopbarRight,
    Tray,
} from '../../elements';
import { Spinner } from '../../spinners';
import { useUserHasRole } from '../../utils/authorization/useUserHasRole';
import { useSearch } from '../../utils/useSearch';
import { InspectionStandardSelect } from '../InspectionStandardSelect';

interface ISearchAttributes {
    objectTypeId?: string;
    inspectionStandardId?: string;
    templateId?: string;
}

export function InspectionFormsList() {
    const t = useTranslate();
    const navigate = useNavigate();
    const { userHasRole } = useUserHasRole();
    const isAdmin = userHasRole([Roles.Administrator]);
    const { searchParameters, setSearchAttribute } = useSearch<ISearchAttributes>({}, []);
    const { data, isLoading, reload } = useOpenApiFetch('/api/settings/inspection-forms', 'get', { searchParameters }, []);
    const [templates, setTemplates] = useState<InspectionTemplate[]>([]);
    const [isTemplatesLoading, setIsTemplatesLoading] = useState(true);

    useEffect(() => {
        setIsTemplatesLoading(true);
        void fetchGet('/api/settings/inspection-templates', {})
            .then((result) => setTemplates(result.json))
            .finally(() => setIsTemplatesLoading(false));
    }, []);

    const removeItem = (inspectionFormId: string) => async () => {
        await openApiFetchDelete('/api/settings/inspection-forms/{inspectionFormId}', { inspectionFormId });
        await reload();
    };

    if (!isAdmin) {
        return <Navigate to="/" />;
    }

    return (
        <>
            <Topbar>
                <TopbarLeft>
                    <TopbarCrumbActive title={t('inspectionForms.title')} icon="fa-cogs" />
                </TopbarLeft>

                <TopbarRight>
                    <PanelRouterLink
                        to="/settings/inspection-forms/add"
                        text={t('common.addItem', t('inspectionForms.form'))}
                        icon="fa fa-plus"
                        small={true}
                    />
                </TopbarRight>
            </Topbar>

            <Content>
                <Tray>
                    <PanelWithoutForm>
                        <PanelBody noPadding={true}>
                            <Table hover={true}>
                                <TableHead>
                                    <TableHeadRow light={true}>
                                        <TableHeader>{t('common.name')}</TableHeader>
                                        <TableHeader>{t('common.objectType')}</TableHeader>
                                        <TableHeader>{t('common.inspectionStandard')}</TableHeader>
                                        <TableHeader>{t('inspectionForms.template')}</TableHeader>
                                        <TableHeader align="right">&nbsp; </TableHeader>
                                    </TableHeadRow>
                                </TableHead>
                                {isLoading || !data ? (
                                    <TableBody>
                                        <TableRow>
                                            <TableData colSpan={2}>
                                                <Spinner />
                                            </TableData>
                                        </TableRow>
                                    </TableBody>
                                ) : (
                                    <TableBody>
                                        {data.map((item) => (
                                            <TableRow key={item.id}>
                                                <TableData>{item.name}</TableData>
                                                <TableData>{item.objectType}</TableData>
                                                <TableData>{item.standard}</TableData>
                                                <TableData>{item.template}</TableData>
                                                <TableData>
                                                    <IconEditButton onClick={() => navigate(`/settings/inspection-forms/${item.id}/edit`)} />
                                                    <IconRemoveButton
                                                        onClick={removeItem(item.id)}
                                                        removeQuestion={t('common.confirmDelete', item.name)}
                                                    />
                                                    <Link
                                                        to={`/settings/inspection-forms/${item.id}/companies`}
                                                        className="btn btn-xs ml5 btn-default"
                                                        style={{ float: 'right' }}
                                                    >
                                                        {t('common.companies')}
                                                    </Link>
                                                </TableData>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                )}
                            </Table>
                        </PanelBody>
                    </PanelWithoutForm>
                </Tray>

                <RightTray>
                    <div className="admin-form">
                        <h4> {t('inspectionForms.filter')}</h4>

                        <InspectionStandardSelect
                            objectTypeId={searchParameters.objectTypeId}
                            inspectionStandardId={searchParameters.inspectionStandardId}
                            onObjectTypeIdChange={(objectTypeId) => setSearchAttribute({ objectTypeId })}
                            onInspectionStandardIdChange={(inspectionStandardId) => setSearchAttribute({ inspectionStandardId })}
                        />

                        <EditPanelSection>
                            <EditPanelSelect
                                name="templateId"
                                value={searchParameters.templateId}
                                handleChange={(templateId: string) => setSearchAttribute({ templateId })}
                                disabled={isTemplatesLoading}
                                topLabel={t('inspectionForms.template')}
                                placeholder={t('common.selectItem', t('inspectionForms.template'))}
                                options={templates.map((template) => ({ value: template.id, text: template.name }))}
                            />
                        </EditPanelSection>
                    </div>
                </RightTray>
            </Content>
        </>
    );
}
